import {useEffect, useRef} from 'react';
import PropTypes from 'prop-types';

const YJS_CAUGHT_ERROR_PREFIX = 'Caught error while handling a Yjs update';

const isYjsGetError = (error) =>
  error instanceof TypeError &&
  error.message.includes('Cannot read properties of undefined') &&
  error.message.includes("reading 'get'");

const YjsErrorBoundary = ({children}) => {
  const hasReloadedRef = useRef(false);

  useEffect(() => {
    const triggerReload = () => {
      if (!hasReloadedRef.current) {
        hasReloadedRef.current = true;
        console.warn('Yjs race condition detected, reloading page...');
        window.location.reload();
      }
    };

    const handleError = (event) => {
      const error = event.error || event.reason;

      if (isYjsGetError(error)) {
        event.preventDefault();
        event.stopPropagation();
        triggerReload();
        return true;
      }

      return false;
    };

    const originalConsoleError = console.error;
    console.error = function (...args) {
      originalConsoleError.apply(console, args);

      const firstArg = args[0];
      if (
        typeof firstArg === 'string' &&
        firstArg.startsWith(YJS_CAUGHT_ERROR_PREFIX) &&
        isYjsGetError(args[1])
      ) {
        triggerReload();
      }
    };

    window.addEventListener('error', handleError, true);
    window.addEventListener('unhandledrejection', handleError, true);

    return () => {
      console.error = originalConsoleError;
      window.removeEventListener('error', handleError, true);
      window.removeEventListener('unhandledrejection', handleError, true);
    };
  }, []);

  return children;
};

YjsErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
};

export default YjsErrorBoundary;
